import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Flex, Menu, TimeRange as KibbleTimeRange, VStack, Wrap } from '@televet/kibble-ui';
import { TimeRange } from './ExclusionsModal';
import { getDateFromTimeString, getUTCTimeStringFromDate, sanitizeDate } from '../utils';
import useClinicUser from 'shared/hooks/useClinicUser';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import isEqual from 'lodash-es/isEqual';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import getTimezoneOffset from 'date-fns-tz/getTimezoneOffset';
import add from 'date-fns/add';
import { GraphQLFetchPolicies } from 'shared/enums';
import { useGetClinicTimezoneNameQuery } from 'shared/types/graphql';
import { Tag } from '@televet/kibble-ui/build/components/Tag';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import cuid from 'cuid';

export interface GroupedTimeRanges {
  id: string;
  startAt: string;
  endAt: string;
  daysOfWeek: number[]; // Representing days of the week as numbers (0 = Sunday, 6 = Saturday)
  timeRanges: TimeRange[];
  isDeleted?: boolean;
}

interface ExclusionaryTimeSlotProps {
  groupedTimeRanges: GroupedTimeRanges;
  startAt: Date;
  endAt: Date;
  setGroupedTimeRanges: React.Dispatch<React.SetStateAction<GroupedTimeRanges[]>>;
  setErrorMap: React.Dispatch<React.SetStateAction<Map<string, string | ReactElement>>>;
  setTimeRangeError: React.Dispatch<React.SetStateAction<string | ReactElement>>;
}

const dayOfWeek = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

const ExclusionaryTimeSlot = ({
  groupedTimeRanges,
  startAt,
  endAt,
  setGroupedTimeRanges,
  setErrorMap,
  setTimeRangeError,
}: ExclusionaryTimeSlotProps): JSX.Element => {
  const [invalidField, setInvalidField] = useState<'start' | 'end' | undefined>();
  const { currentClinicId } = useClinicUser();

  const { data: timezoneNameData } = useGetClinicTimezoneNameQuery({
    variables: {
      clinicId: currentClinicId || '',
    },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  const dayOfWeekOptions = useMemo(() => {
    return dayOfWeek.map((day) => ({
      ...day,
      isSelected: groupedTimeRanges.daysOfWeek.includes(parseInt(day.value, 10)),
    }));
  }, [groupedTimeRanges.daysOfWeek]);

  const clinicTimezoneName = useMemo(
    () => timezoneNameData?.findUniqueClinic?.timezoneName || '',
    [timezoneNameData?.findUniqueClinic?.timezoneName],
  );

  const localToClinicTimezone = useCallback(
    (date: Date, reverse?: boolean): Date => {
      const localTzOffset = getTimezoneOffset(Intl.DateTimeFormat().resolvedOptions().timeZone);
      const clinicTzOffset = getTimezoneOffset(clinicTimezoneName);
      const offsetDiff = reverse ? clinicTzOffset - localTzOffset : localTzOffset - clinicTzOffset;
      return add(date, { minutes: offsetDiff / 1000 / 60 });
    },
    [clinicTimezoneName],
  );

  const validateTimeSlot = useCallback(() => {
    const startVsEnd = differenceInMinutes(
      sanitizeDate(localToClinicTimezone(startAt, true)),
      sanitizeDate(localToClinicTimezone(endAt, true)),
    );
    if (startVsEnd > 0) {
      setErrorMap((current) => {
        current.set(groupedTimeRanges.id, 'You must select an end time that comes after the start time');
        const firstError = Array.from(current.values()).find((err) => !!err) || '';
        setTimeRangeError(firstError);
        return current;
      });
      setInvalidField('end');
      return;
    }

    setErrorMap((current) => {
      current.set(groupedTimeRanges.id, '');
      const firstError = Array.from(current.values()).find((err) => !!err) || '';
      setTimeRangeError(firstError);
      return current;
    });
    setInvalidField(undefined);
  }, [localToClinicTimezone, groupedTimeRanges.id, startAt, endAt, setErrorMap, setTimeRangeError]);

  useEffect(validateTimeSlot, [validateTimeSlot]);

  const handleFieldChange = useCallback(
    (newValue: string, field: 'dayOfWeek' | 'startAt' | 'endAt') => {
      setGroupedTimeRanges((currentGroups) => {
        return currentGroups.map((group) => {
          // Only modify the current group using its unique ID
          if (group.id !== groupedTimeRanges.id) {
            return group;
          }
          let newRanges;
          // if it's not dayOfWeek, we can update the existing grouped time ranges
          if (field !== 'dayOfWeek') {
            newRanges = group.timeRanges.map((range) => {
              return {
                ...range,
                updatedAt: new Date(),
                [field]: newValue,
              };
            });
          } else {
            const dayOfWeek = parseInt(newValue, 10);
            const existingRange = group.timeRanges.find((r) => r.dayOfWeek === dayOfWeek);
            // if the dayOfWeek already exists, we either remove it or mark it as deleted
            if (existingRange?.isNew) {
              newRanges = group.timeRanges.filter((range) => range.id !== existingRange.id);
            } else if (existingRange) {
              newRanges = group.timeRanges.map((range) => {
                if (range.id === existingRange.id) {
                  return {
                    ...range,
                    updatedAt: new Date(),
                    isDeleted: !range.isDeleted,
                  };
                }
                return range;
              });
              // if the dayOfWeek does not exist, we add a new time range
            } else {
              newRanges = [
                ...group.timeRanges,
                {
                  id: `newRange-${cuid()}}`,
                  startAt: getUTCTimeStringFromDate(startAt),
                  endAt: getUTCTimeStringFromDate(endAt),
                  dayOfWeek: dayOfWeek,
                  isNew: true,
                  updatedAt: new Date(),
                },
              ];
            }
          }

          /**
           * We must update all time values based on today's date because the UTC
           * offset of `updatedAt`, which is required to know the user's intended
           * time of day, may have changed since the last time these were set
           * (e.g. inside or outside of DST).
           */
          const now = new Date();
          const timeRanges = newRanges.map((range) => {
            const originalStartAt = getDateFromTimeString(range.startAt, range.updatedAt);
            const originalEndAt = getDateFromTimeString(range.endAt, range.updatedAt);

            const startAtTimezoneOffsetDiff =
              getTimezoneOffset(clinicTimezoneName, now) - getTimezoneOffset(clinicTimezoneName, originalStartAt);
            const endAtTimezoneOffsetDiff =
              getTimezoneOffset(clinicTimezoneName, now) - getTimezoneOffset(clinicTimezoneName, originalEndAt);

            const newStartAtDate = new Date(
              getDateFromTimeString(range.startAt, now).getTime() - startAtTimezoneOffsetDiff,
            );
            const newEndAtDate = new Date(getDateFromTimeString(range.endAt, now).getTime() - endAtTimezoneOffsetDiff);

            return {
              ...range,
              updatedAt: new Date(),
              startAt: getUTCTimeStringFromDate(newStartAtDate),
              endAt: getUTCTimeStringFromDate(newEndAtDate),
            };
          });
          return {
            ...group,
            ...(field !== 'dayOfWeek' && { [field]: newValue }),
            ...(field === 'dayOfWeek' && {
              daysOfWeek: newRanges.filter((r) => !r.isDeleted).map((range) => range.dayOfWeek),
            }),
            timeRanges,
          };
        });
      });
    },
    [clinicTimezoneName, endAt, groupedTimeRanges, setGroupedTimeRanges, startAt],
  );

  const handleRemoveTimeRange = useCallback(() => {
    setGroupedTimeRanges((currentGroups) => {
      return currentGroups.map((group) => {
        // Only modify the current group using its unique ID
        if (group.id !== groupedTimeRanges.id) {
          return group;
        }

        // Mark the group as deleted
        // Filter out time ranges that are new, mark existing ones as deleted
        const updatedTimeRanges = group.timeRanges
          .filter((range) => !range.isNew) // Remove new ranges completely
          .map((range) => ({
            ...range,
            isDeleted: true, // Mark existing ranges as deleted
          }));

        return {
          ...group,
          isDeleted: true,
          timeRanges: updatedTimeRanges,
        };
      });
    });
    setErrorMap((current) => {
      current.delete(groupedTimeRanges.id);
      const firstError = Array.from(current.values()).find((err) => !!err) || '';
      setTimeRangeError(firstError);
      return current;
    });
  }, [groupedTimeRanges.id, setErrorMap, setTimeRangeError, setGroupedTimeRanges]);

  const handleSelectAll = useCallback(
    (selectedItems): void => {
      let daysToChange = [];
      if (!selectedItems?.length) {
        daysToChange = groupedTimeRanges.daysOfWeek.map((day) => day.toString());
      } else {
        daysToChange = dayOfWeekOptions
          .filter((option) => !groupedTimeRanges.daysOfWeek.includes(parseInt(option.value, 10)))
          .map((option) => option.value);
      }
      console.log(daysToChange);
      for (const day of daysToChange) {
        handleFieldChange(day, 'dayOfWeek');
      }
    },
    [dayOfWeekOptions, groupedTimeRanges.daysOfWeek, handleFieldChange],
  );

  const sortedDaysOfWeek = useMemo(() => {
    return groupedTimeRanges.daysOfWeek.sort((a, b) => a - b);
  }, [groupedTimeRanges.daysOfWeek]);

  return (
    <VStack alignItems="flex-start" backgroundColor="background.subtle" p={3} borderRadius="md" borderWidth="1px">
      <Flex alignItems="center" justifyContent="center" w="100%" gap={2}>
        <KibbleTimeRange
          startTimePickerProps={{
            initialTime: utcToZonedTime(startAt, clinicTimezoneName),
            onTimeChange: (date: Date): void => {
              const adjustedDate = localToClinicTimezone(date);
              handleFieldChange(getUTCTimeStringFromDate(adjustedDate), 'startAt');
            },
            inputProps: {
              width: '130px',
              isInvalid: invalidField === 'start',
              errorText: '',
            },
          }}
          endTimePickerProps={{
            initialTime: utcToZonedTime(endAt, clinicTimezoneName),
            onTimeChange: (date: Date): void => {
              const adjustedDate = localToClinicTimezone(date);
              handleFieldChange(getUTCTimeStringFromDate(adjustedDate), 'endAt');
            },
            inputProps: {
              width: '130px',
              isInvalid: invalidField === 'end',
              errorText: '',
            },
          }}
        />
        <Menu
          buttonProps={{
            variant: 'secondarySubtle',
            leftIconName: 'plus',
            size: 'sm',
            text: 'Add Days',
            width: 'max-content',
            showRightIcon: false,
          }}
          listProps={{
            menuItems: dayOfWeekOptions,
            isSearchable: true,
            isMultiSelect: true,
            canSelectAll: true,
            onSelect: (option): void => {
              handleFieldChange(option.value as string, 'dayOfWeek');
            },
            onSelectAll: handleSelectAll,
            maxH: '250px',
          }}
        />
        <Button iconName="trashcan" variant="ghostNeutral" onClick={handleRemoveTimeRange} ml="auto" />
      </Flex>
      <Wrap>
        {sortedDaysOfWeek.map((value) => {
          const selectedDay = dayOfWeekOptions.find((option) => option.value === value.toString());
          return (
            <Tag
              key={value}
              label={selectedDay?.label || ''}
              rightElement={
                <Icon
                  name="close"
                  size="sm"
                  onClick={(): void => handleFieldChange(value.toString(), 'dayOfWeek')}
                  cursor="pointer"
                />
              }
            />
          );
        })}
      </Wrap>
    </VStack>
  );
};

// don't rerender each time slot whenever any of them changes
function propCompare(prev: ExclusionaryTimeSlotProps, next: ExclusionaryTimeSlotProps): boolean {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { setGroupedTimeRanges: prevSetGroupedTimeRanges, setErrorMap: prevSetErrorMap, ...prevToCompare } = prev;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { setGroupedTimeRanges, setErrorMap, ...nextToCompare } = next;
  return isEqual(prevToCompare, nextToCompare);
}

export default React.memo(ExclusionaryTimeSlot, propCompare);
